import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingBag,
  Sparkles,
  IndianRupee,
  RefreshCw,
  Calendar,
  TrendingUp,
  TrendingDown,
  Layers,
  ArrowRight,
  Award,
} from "lucide-react";

const API_BASE = "https://api.vanyacollectionsrajahmundry.com";

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Date Filter State
  const [datePreset, setDatePreset] = useState("all"); // 'today', 'yesterday', 'week', 'month', 'all', 'custom'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Chart hover state
  const [hoveredDataPoint, setHoveredDataPoint] = useState(null);

  // ==========================================
  // FETCH ALL DATA
  // ==========================================
  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [ordersRes, productsRes] = await Promise.all([
        fetch(`${API_BASE}/orders/all`)
          .then((r) => r.json())
          .catch(() => []),
        fetch(`${API_BASE}/products/all?admin=true`)
          .then((r) => r.json())
          .catch(() => []),
      ]);

      const ordersList = Array.isArray(ordersRes)
        ? ordersRes
        : ordersRes.orders || [];

      const productsList = Array.isArray(productsRes)
        ? productsRes
        : productsRes.products || productsRes.data || [];

      setOrders(ordersList);
      setProducts(productsList);
    } catch (err) {
      console.error("Dashboard data load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // ==========================================
  // DATE FORMATTING HELPERS
  // ==========================================
  const formatDateDDMMYYYY = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTimeDDMMYYYY = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");
    return `${day}-${month}-${year} at ${formattedHours}:${minutes} ${ampm}`;
  };

  const getOrderCode = (order) => {
    if (!order) return "";
    return order.order_number || (order.id < 10 ? `VAN-0${order.id}` : `VAN-${order.id}`);
  };

  const getOrderStatusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "delivered":
        return { background: "#d1e7dd", color: "#0f5132" };
      case "shipped":
        return { background: "#fff3cd", color: "#664d03" };
      case "packed":
        return { background: "#cff4fc", color: "#055160" };
      case "confirmed":
        return { background: "#e8f5e9", color: "#2e7d32" };
      case "cancelled":
      case "payment_rejected":
        return { background: "#f8d7da", color: "#842029" };
      default:
        return { background: "#f3e8ff", color: "#6b21a8" };
    }
  };

  // ==========================================
  // DATE PRESET HANDLER
  // ==========================================
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    const toISODate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "today") {
      const todayStr = toISODate(now);
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === "yesterday") {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yStr = toISODate(y);
      setStartDate(yStr);
      setEndDate(yStr);
    } else if (preset === "week") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      setStartDate(toISODate(weekAgo));
      setEndDate(toISODate(now));
    } else if (preset === "month") {
      const monthAgo = new Date(now);
      monthAgo.setDate(monthAgo.getDate() - 30);
      setStartDate(toISODate(monthAgo));
      setEndDate(toISODate(now));
    }
  };

  // ==========================================
  // TODAY'S STATS (DEDICATED)
  // ==========================================
  const todayStats = useMemo(() => {
    const now = new Date();
    const todayYear = now.getFullYear();
    const todayMonth = now.getMonth();
    const todayDate = now.getDate();

    const todayOrdersList = orders.filter((o) => {
      if (!o.created_at) return false;
      const d = new Date(o.created_at);
      return (
        d.getFullYear() === todayYear &&
        d.getMonth() === todayMonth &&
        d.getDate() === todayDate
      );
    });

    const revenue = todayOrdersList.reduce((acc, curr) => {
      return acc + (parseFloat(curr.total_amount) || 0);
    }, 0);

    const confirmed = todayOrdersList.filter(
      (o) => o.order_status === "confirmed" || o.order_status === "delivered"
    ).length;

    const shipped = todayOrdersList.filter((o) => o.order_status === "shipped").length;

    return {
      count: todayOrdersList.length,
      revenue,
      confirmed,
      shipped,
    };
  }, [orders]);

  // ==========================================
  // FILTERED ORDERS BY ACTIVE DATE RANGE
  // ==========================================
  const filteredOrders = useMemo(() => {
    if (!startDate && !endDate) return orders;

    return orders.filter((ord) => {
      if (!ord.created_at) return false;
      const orderDate = new Date(ord.created_at);
      if (isNaN(orderDate.getTime())) return false;

      if (startDate) {
        const start = new Date(startDate + "T00:00:00");
        if (orderDate < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate + "T23:59:59.999");
        if (orderDate > end) return false;
      }
      return true;
    });
  }, [orders, startDate, endDate]);


  // Period / Filtered Metrics
  const periodStats = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce(
      (acc, o) => acc + (parseFloat(o.total_amount) || 0),
      0
    );

    const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    let confirmedCount = 0;
    let packedCount = 0;
    let shippedCount = 0;
    let deliveredCount = 0;
    let cancelledCount = 0;
    let pendingCount = 0;

    let rzpRevenue = 0;
    let rzpCount = 0;
    let codRevenue = 0;
    let codCount = 0;
    let upiRevenue = 0;
    let upiCount = 0;

    filteredOrders.forEach((o) => {
      const st = (o.order_status || "pending").toLowerCase();
      const amt = parseFloat(o.total_amount) || 0;
      const pm = (o.payment_method || "").toLowerCase();

      if (st === "confirmed") confirmedCount++;
      else if (st === "packed") packedCount++;
      else if (st === "shipped") shippedCount++;
      else if (st === "delivered") deliveredCount++;
      else if (st === "cancelled" || st === "payment_rejected") cancelledCount++;
      else pendingCount++;

      if (pm === "razorpay") {
        rzpRevenue += amt;
        rzpCount++;
      } else if (pm === "cod") {
        codRevenue += amt;
        codCount++;
      } else {
        upiRevenue += amt;
        upiCount++;
      }
    });

    const successRate =
      totalOrders > 0
        ? Math.round(
            ((confirmedCount + packedCount + shippedCount + deliveredCount) /
              totalOrders) *
              100
          )
        : 0;

    return {
      totalOrders,
      totalRevenue,
      aov,
      confirmedCount,
      packedCount,
      shippedCount,
      deliveredCount,
      cancelledCount,
      pendingCount,
      successRate,
      payments: {
        razorpay: { count: rzpCount, amount: rzpRevenue },
        cod: { count: codCount, amount: codRevenue },
        upi: { count: upiCount, amount: upiRevenue },
      },
    };
  }, [filteredOrders]);

  // ==========================================
  // ==========================================
  // HIGHEST & LEAST SELLING PRODUCTS (AGGREGATED BY PRODUCT ID)
  // ==========================================
  const productPerformance = useMemo(() => {
    const productSalesMap = {};

    const extractPid = (it) => {
      if (!it) return "";
      if (it.product_id) return String(it.product_id);
      if (it.productId) return String(it.productId);
      if (it.id && !String(it.id).startsWith("guest_")) return String(it.id);
      return "";
    };

    // 1. Seed with catalog products by their distinct product ID
    if (Array.isArray(products)) {
      products.forEach((p) => {
        const pid = String(p.id);
        if (!pid) return;

        const colour =
          p.colour ||
          (Array.isArray(p.variants) && p.variants[0] && p.variants[0].colour) ||
          "";

        const img =
          p.img_url ||
          (Array.isArray(p.thumbnails) && p.thumbnails[0]) ||
          (Array.isArray(p.variants) &&
            p.variants[0] &&
            (p.variants[0].mainImage || p.variants[0].img_url)) ||
          "";

        productSalesMap[pid] = {
          id: pid,
          productId: pid,
          name: p.name || "Dress Material",
          colour: colour,
          price: parseFloat(p.price) || 0,
          stock: p.stock ?? 0,
          img: img,
          category: p.category || "Dress Materials",
          unitsSold: 0,
          revenue: 0,
          orderCount: 0,
        };
      });
    }

    // 2. Also seed from all historical orders
    if (Array.isArray(orders)) {
      orders.forEach((ord) => {
        let itemsList = [];
        try {
          itemsList = typeof ord.items === "string" ? JSON.parse(ord.items) : ord.items || [];
        } catch {
          itemsList = [];
        }
        if (!Array.isArray(itemsList)) itemsList = [];

        itemsList.forEach((it) => {
          const pid = extractPid(it) || (it.name ? it.name.trim().toLowerCase() : "");
          if (!pid) return;

          const colour = it.colour || (it.variant && it.variant.colour) || "";
          const img =
            it.img ||
            it.img_url ||
            it.image ||
            it.imageUrl ||
            it.mainImage ||
            (it.variant && (it.variant.mainImage || it.variant.img_url || it.variant.image)) ||
            "";

          if (!productSalesMap[pid]) {
            productSalesMap[pid] = {
              id: pid,
              productId: pid,
              name: it.name || "Dress Material",
              colour: colour,
              price: parseFloat(it.price) || 0,
              stock: it.stock ?? 0,
              img: img,
              category: it.category || "Dress Materials",
              unitsSold: 0,
              revenue: 0,
              orderCount: 0,
            };
          } else {
            if (!productSalesMap[pid].colour && colour) productSalesMap[pid].colour = colour;
            if (!productSalesMap[pid].img && img) productSalesMap[pid].img = img;
          }
        });
      });
    }

    // 3. Aggregate sales for the CURRENT FILTERED period by Product ID
    if (Array.isArray(filteredOrders)) {
      filteredOrders.forEach((order) => {
        let itemsList = [];
        try {
          itemsList = typeof order.items === "string" ? JSON.parse(order.items) : order.items || [];
        } catch {
          itemsList = [];
        }
        if (!Array.isArray(itemsList)) itemsList = [];

        itemsList.forEach((item) => {
          const pid = extractPid(item) || (item.name ? item.name.trim().toLowerCase() : "");
          if (!pid) return;

          const qty = parseInt(item.quantity, 10) || 1;
          const price =
            parseFloat(item.price) || (productSalesMap[pid] ? productSalesMap[pid].price : 0);
          const revenue = price * qty;
          const colour = item.colour || (item.variant && item.variant.colour) || "";
          const img =
            item.img ||
            item.img_url ||
            item.image ||
            item.imageUrl ||
            item.mainImage ||
            (item.variant && (item.variant.mainImage || item.variant.img_url || item.variant.image)) ||
            "";

          if (!productSalesMap[pid]) {
            productSalesMap[pid] = {
              id: pid,
              productId: pid,
              name: item.name || "Dress Material",
              colour: colour,
              price: price,
              stock: item.stock ?? 0,
              img: img,
              category: item.category || "Dress Materials",
              unitsSold: 0,
              revenue: 0,
              orderCount: 0,
            };
          } else {
            if (!productSalesMap[pid].colour && colour) productSalesMap[pid].colour = colour;
            if (!productSalesMap[pid].img && img) productSalesMap[pid].img = img;
          }

          productSalesMap[pid].unitsSold += qty;
          productSalesMap[pid].revenue += revenue;
          productSalesMap[pid].orderCount += 1;
        });
      });
    }

    const allProductsArray = Object.values(productSalesMap);

    // 4. Highest Selling: items with unitsSold > 0, sorted descending by units sold
    const highestSelling = allProductsArray
      .filter((p) => p.unitsSold > 0)
      .sort((a, b) => b.unitsSold - a.unitsSold || b.revenue - a.revenue)
      .slice(0, 5);

    // IDs of highest selling products to EXCLUDE from least selling
    const highestSellingIds = new Set(highestSelling.map((p) => String(p.id)));

    // 5. Least Selling: items with lowest sales (or 0 sales) that are NOT in highest selling!
    let remainingCandidates = allProductsArray.filter(
      (p) => !highestSellingIds.has(String(p.id))
    );

    if (remainingCandidates.length === 0 && allProductsArray.length > 1) {
      remainingCandidates = allProductsArray.slice(1);
    }

    const leastSelling = remainingCandidates
      .sort((a, b) => a.unitsSold - b.unitsSold || a.revenue - b.revenue)
      .slice(0, 5);

    return {
      highestSelling,
      leastSelling,
    };
  }, [products, orders, filteredOrders]);

  // ==========================================
  // TIMELINE CHART DATA GENERATION
  // ==========================================
  const chartData = useMemo(() => {
    // Generate daily points for the last 14 days or the filtered range
    let daysToInclude = 10;
    if (datePreset === "month") daysToInclude = 30;
    else if (datePreset === "week") daysToInclude = 7;
    else if (datePreset === "today" || datePreset === "yesterday") daysToInclude = 1;

    const points = [];
    const now = new Date();

    for (let i = daysToInclude - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dayStr = `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}`;

      const year = d.getFullYear();
      const month = d.getMonth();
      const date = d.getDate();

      const dayOrders = filteredOrders.filter((ord) => {
        if (!ord.created_at) return false;
        const od = new Date(ord.created_at);
        return (
          od.getFullYear() === year &&
          od.getMonth() === month &&
          od.getDate() === date
        );
      });

      const dayRevenue = dayOrders.reduce(
        (acc, o) => acc + (parseFloat(o.total_amount) || 0),
        0
      );

      points.push({
        label: dayStr,
        fullDate: formatDateDDMMYYYY(d),
        orders: dayOrders.length,
        revenue: dayRevenue,
      });
    }

    const maxRevenue = Math.max(...points.map((p) => p.revenue), 1000);
    const maxOrders = Math.max(...points.map((p) => p.orders), 5);

    return { points, maxRevenue, maxOrders };
  }, [filteredOrders, datePreset]);

  // Recent 8 orders for preview table
  const recentOrders = useMemo(() => {
    return filteredOrders.slice(0, 8);
  }, [filteredOrders]);

  return (
    <div
      className="dashboard-container"
      style={{
        maxWidth: 1400,
        margin: "0 auto",
        padding: "24px 28px",
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        background: "#f4f6f8",
        minHeight: "100vh",
      }}
    >
      {/* ==============================================================
          HEADER BAR
      ============================================================== */}
      <div
        className="dashboard-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#ecfdf5",
              color: "#065f46",
              padding: "4px 10px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              marginBottom: 6,
              border: "1px solid #a7f3d0",
            }}
          >
            <Sparkles size={14} color="#d97706" />
            <span>EXECUTIVE ANALYTICS</span>
          </div>
          <h1
            style={{
              fontFamily: "Playfair Display, serif",
              color: "#063b2a",
              margin: 0,
              fontSize: 28,
            }}
          >
            Vanya Collections Dashboard
          </h1>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
            Real-time sales, order volume, revenue collection, and catalog performance overview.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            className="dashboard-header-btn"
            onClick={fetchDashboardData}
            disabled={loading}
            style={{
              background: "#063b2a",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 18px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "bold",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 2px 6px rgba(6,59,42,0.2)",
            }}
          >
            <RefreshCw size={14} className={loading ? "spin-icon" : ""} />
            <span>{loading ? "Refreshing..." : "↻ Refresh Data"}</span>
          </button>
        </div>
      </div>

      {/* ==============================================================
          DATE RANGE FILTER TOOLBAR
      ============================================================== */}
      <div
        className="dashboard-filter-card"
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "14px 18px",
          marginBottom: 24,
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div className="dashboard-filter-presets" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
            <Calendar size={15} color="#063b2a" />
            Filter Period:
          </span>

          {[
            { id: "today", label: "Today" },
            { id: "yesterday", label: "Yesterday" },
            { id: "week", label: "Last 7 Days" },
            { id: "month", label: "This Month" },
            { id: "all", label: "All Time" },
            { id: "custom", label: "Custom Range" },
          ].map((p) => {
            const isActive = datePreset === p.id;
            return (
              <button
                key={p.id}
                onClick={() => handleDatePreset(p.id)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 20,
                  border: isActive ? "1.5px solid #063b2a" : "1px solid #d1d5db",
                  background: isActive ? "#063b2a" : "#fff",
                  color: isActive ? "#fff" : "#4b5563",
                  fontSize: 12,
                  cursor: "pointer",
                  fontWeight: isActive ? 700 : 500,
                  transition: "all 0.15s ease",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Custom date range inputs */}
        <div className="dashboard-filter-dates" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 11, color: "#666" }}>From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setDatePreset("custom");
              }}
              style={{
                padding: "5px 8px",
                fontSize: 12,
                border: "1px solid #d1d5db",
                borderRadius: 6,
                background: "#fafafa",
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 11, color: "#666" }}>To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setDatePreset("custom");
              }}
              style={{
                padding: "5px 8px",
                fontSize: 12,
                border: "1px solid #d1d5db",
                borderRadius: 6,
                background: "#fafafa",
              }}
            />
          </div>

          {(startDate || endDate) && (
            <button
              onClick={() => handleDatePreset("all")}
              style={{
                background: "transparent",
                border: "none",
                color: "#dc2626",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* ==============================================================
          METRIC CARDS (TODAY'S STATS + FILTERED PERIOD + OVERALL)
      ============================================================== */}
      <div
        className="dashboard-stats-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* TODAY'S REVENUE / AMOUNT COLLECTED */}
        <div
          className="dashboard-stat-card"
          style={{
            ...statCardStyle,
            background: "linear-gradient(135deg, #063b2a 0%, #0d543d 100%)",
            color: "#fff",
            borderColor: "#063b2a",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ ...statCardLabel, color: "#d1fae5" }}>TODAY'S REVENUE</span>
            <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 8, padding: 6 }}>
              <IndianRupee size={18} color="#fff" />
            </div>
          </div>
          <div className="dashboard-stat-value" style={{ fontSize: 28, fontWeight: "bold", margin: "8px 0 4px" }}>
            ₹{todayStats.revenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
          </div>
          <div className="dashboard-stat-sub" style={{ fontSize: 12, color: "#a7f3d0", display: "flex", alignItems: "center", gap: 5 }}>
            <TrendingUp size={14} />
            <span>Amount collected today</span>
          </div>
        </div>

        {/* TODAY'S ORDERS COUNT */}
        <div
          className="dashboard-stat-card"
          style={{
            ...statCardStyle,
            borderLeft: "4px solid #0284c7",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={statCardLabel}>TODAY'S BOOKINGS</span>
            <div style={{ background: "#f0f9ff", borderRadius: 8, padding: 6, color: "#0284c7" }}>
              <ShoppingBag size={18} />
            </div>
          </div>
          <div className="dashboard-stat-value" style={{ fontSize: 28, fontWeight: "bold", color: "#0f172a", margin: "8px 0 4px" }}>
            {todayStats.count} <span style={{ fontSize: 14, fontWeight: 500, color: "#64748b" }}>orders</span>
          </div>
          <div className="dashboard-stat-sub" style={{ fontSize: 12, color: "#0284c7", fontWeight: 600 }}>
            {todayStats.confirmed} confirmed • {todayStats.shipped} dispatched
          </div>
        </div>

        {/* FILTERED PERIOD REVENUE */}
        <div
          className="dashboard-stat-card"
          style={{
            ...statCardStyle,
            borderLeft: "4px solid #d97706",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={statCardLabel}>
              {datePreset === "all" ? "OVERALL REVENUE" : "PERIOD REVENUE"}
            </span>
            <div style={{ background: "#fffbeb", borderRadius: 8, padding: 6, color: "#d97706" }}>
              <IndianRupee size={18} />
            </div>
          </div>
          <div className="dashboard-stat-value" style={{ fontSize: 28, fontWeight: "bold", color: "#0f172a", margin: "8px 0 4px" }}>
            ₹{periodStats.totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
          </div>
          <div className="dashboard-stat-sub" style={{ fontSize: 12, color: "#64748b" }}>
            {datePreset === "all" ? "Cumulative all-time sales" : `Sales in selected filter range`}
          </div>
        </div>

        {/* FILTERED ORDERS COUNT & AOV */}
        <div
          className="dashboard-stat-card"
          style={{
            ...statCardStyle,
            borderLeft: "4px solid #10b981",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={statCardLabel}>
              {datePreset === "all" ? "TOTAL ORDERS" : "PERIOD ORDERS"}
            </span>
            <div style={{ background: "#ecfdf5", borderRadius: 8, padding: 6, color: "#10b981" }}>
              <Layers size={18} />
            </div>
          </div>
          <div className="dashboard-stat-value" style={{ fontSize: 28, fontWeight: "bold", color: "#0f172a", margin: "8px 0 4px" }}>
            {periodStats.totalOrders}
          </div>
          <div className="dashboard-stat-sub" style={{ fontSize: 12, color: "#64748b" }}>
            Avg Order Value:{" "}
            <strong style={{ color: "#063b2a" }}>
              ₹{Math.round(periodStats.aov).toLocaleString("en-IN")}
            </strong>
          </div>
        </div>
      </div>

      {/* ==============================================================
          GRAPHS & VISUAL ANALYTICS ROW
      ============================================================== */}
      <div
        className="dashboard-analytics-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        {/* GRAPH 1: TIMELINE REVENUE & ORDERS */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 20,
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, color: "#063b2a" }}>Sales & Order Volume Trend</h3>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                Daily revenue collections and bookings over the timeline
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, fontWeight: 600 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#063b2a" }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: "#063b2a" }}></span> Revenue
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#d97706" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#d97706" }}></span> Orders
              </span>
            </div>
          </div>

          {/* SVG TIMELINE BAR & LINE CHART */}
          <div style={{ width: "100%", height: 210, position: "relative" }}>
            <svg
              viewBox={`0 0 ${chartData.points.length * 60} 200`}
              style={{ width: "100%", height: "100%", overflow: "visible" }}
            >
              {/* Grid lines */}
              <line x1="0" y1="40" x2={chartData.points.length * 60} y2="40" stroke="#f1f5f9" strokeDasharray="3 3" />
              <line x1="0" y1="90" x2={chartData.points.length * 60} y2="90" stroke="#f1f5f9" strokeDasharray="3 3" />
              <line x1="0" y1="140" x2={chartData.points.length * 60} y2="140" stroke="#f1f5f9" strokeDasharray="3 3" />

              {/* Bars and labels */}
              {chartData.points.map((pt, idx) => {
                const x = idx * 60 + 20;
                const barHeight = Math.max((pt.revenue / chartData.maxRevenue) * 120, 6);
                const y = 160 - barHeight;

                const isHovered = hoveredDataPoint === idx;

                return (
                  <g
                    key={idx}
                    onMouseEnter={() => setHoveredDataPoint(idx)}
                    onMouseLeave={() => setHoveredDataPoint(null)}
                    style={{ cursor: "pointer" }}
                  >
                    {/* Background hover bar */}
                    <rect
                      x={x - 8}
                      y="10"
                      width="40"
                      height="160"
                      fill={isHovered ? "rgba(6,59,42,0.06)" : "transparent"}
                      rx="4"
                    />

                    {/* Revenue Bar */}
                    <rect
                      x={x}
                      y={y}
                      width="24"
                      height={barHeight}
                      fill={isHovered ? "#047857" : "#063b2a"}
                      rx="4"
                      style={{ transition: "all 0.2s ease" }}
                    />

                    {/* Order count circle */}
                    {pt.orders > 0 && (
                      <circle
                        cx={x + 12}
                        cy={y - 10}
                        r="5"
                        fill="#d97706"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                    )}

                    {/* X axis date label */}
                    <text
                      x={x + 12}
                      y="180"
                      textAnchor="middle"
                      fontSize="10"
                      fill="#64748b"
                      fontWeight={isHovered ? "bold" : "normal"}
                    >
                      {pt.label}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Hover Tooltip Overlay */}
            {hoveredDataPoint !== null && (
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  background: "#063b2a",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 12,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              >
                <div style={{ fontWeight: 700, color: "#d1fae5" }}>
                  {chartData.points[hoveredDataPoint]?.fullDate}
                </div>
                <div style={{ marginTop: 2 }}>
                  Revenue: <strong>₹{chartData.points[hoveredDataPoint]?.revenue.toLocaleString("en-IN")}</strong>
                </div>
                <div>
                  Bookings: <strong>{chartData.points[hoveredDataPoint]?.orders} orders</strong>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* GRAPH 2: STATUS & PAYMENT METHOD BREAKDOWN */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 20,
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: "#063b2a" }}>Order Fulfillment & Stages</h3>
              <span style={{ fontSize: 12, color: "#059669", fontWeight: 700 }}>
                {periodStats.successRate}% Success / Active Rate
              </span>
            </div>

            {/* Progress Bars for Stages */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: "Delivered", count: periodStats.deliveredCount, color: "#059669", bg: "#ecfdf5" },
                { label: "Shipped", count: periodStats.shippedCount, color: "#d97706", bg: "#fffbeb" },
                { label: "Packed", count: periodStats.packedCount, color: "#0284c7", bg: "#f0f9ff" },
                { label: "Confirmed", count: periodStats.confirmedCount, color: "#16a34a", bg: "#f0fdf4" },
                { label: "Cancelled", count: periodStats.cancelledCount, color: "#dc2626", bg: "#fef2f2" },
              ].map((stage, i) => {
                const pct =
                  periodStats.totalOrders > 0
                    ? Math.round((stage.count / periodStats.totalOrders) * 100)
                    : 0;

                return (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                      <span style={{ fontWeight: 600, color: "#374151" }}>{stage.label}</span>
                      <span style={{ color: "#6b7280" }}>
                        {stage.count} orders ({pct}%)
                      </span>
                    </div>
                    <div style={{ width: "100%", height: 7, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: "100%",
                          background: stage.color,
                          borderRadius: 4,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Method Badges */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#4b5563", marginBottom: 8 }}>
              PAYMENT METHOD DISTRIBUTION
            </div>
            <div className="dashboard-payment-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <div style={{ background: "#ecfdf5", padding: "8px 10px", borderRadius: 8, border: "1px solid #a7f3d0" }}>
                <div style={{ fontSize: 11, color: "#047857", fontWeight: 700 }}>⚡ Razorpay</div>
                <div style={{ fontSize: 14, fontWeight: "bold", color: "#065f46" }}>
                  ₹{periodStats.payments.razorpay.amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
                <div style={{ fontSize: 10, color: "#059669" }}>{periodStats.payments.razorpay.count} orders</div>
              </div>

              <div style={{ background: "#e0f2fe", padding: "8px 10px", borderRadius: 8, border: "1px solid #bae6fd" }}>
                <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 700 }}>💵 COD</div>
                <div style={{ fontSize: 14, fontWeight: "bold", color: "#075985" }}>
                  ₹{periodStats.payments.cod.amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
                <div style={{ fontSize: 10, color: "#0284c7" }}>{periodStats.payments.cod.count} orders</div>
              </div>

              <div style={{ background: "#f3e8ff", padding: "8px 10px", borderRadius: 8, border: "1px solid #e9d5ff" }}>
                <div style={{ fontSize: 11, color: "#6b21a8", fontWeight: 700 }}>📱 UPI Manual</div>
                <div style={{ fontSize: 14, fontWeight: "bold", color: "#581c87" }}>
                  ₹{periodStats.payments.upi.amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
                <div style={{ fontSize: 10, color: "#7e22ce" }}>{periodStats.payments.upi.count} orders</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==============================================================
          PRODUCT PERFORMANCE ROW (HIGHEST SELLING & LEAST SELLING)
      ============================================================== */}
      <div
        className="dashboard-products-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
          gap: 20,
          marginBottom: 26,
        }}
      >
        {/* HIGHEST SELLING PRODUCTS */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 20,
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ background: "#fef3c7", padding: 6, borderRadius: 8, color: "#d97706" }}>
                <Award size={18} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: "#063b2a" }}>Highest Selling Products</h3>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                  Top performing items by volume ordered
                </p>
              </div>
            </div>

            <Link
              to="/admin/products"
              style={{ fontSize: 12, color: "#063b2a", textDecoration: "none", fontWeight: 700 }}
            >
              Inventory →
            </Link>
          </div>

          {productPerformance.highestSelling.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              No product sales recorded in the selected period.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {productPerformance.highestSelling.map((prod, idx) => (
                <div
                  key={prod.id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: idx === 0 ? "#fffdf5" : "#fafafa",
                    borderRadius: 8,
                    border: idx === 0 ? "1px solid #fde68a" : "1px solid #f1f5f9",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        borderRadius: "50%",
                        background:
                          idx === 0
                            ? "#d97706"
                            : idx === 1
                            ? "#475569"
                            : idx === 2
                            ? "#b45309"
                            : idx === 3
                            ? "#063b2a"
                            : idx === 4
                            ? "#0284c7"
                            : "#334155",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: "700",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                      }}
                    >
                      {idx + 1}
                    </div>

                    {prod.img ? (
                      <img
                        src={prod.img}
                        alt={prod.name}
                        style={{
                          width: 42,
                          height: 48,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #e2e8f0",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 42,
                          height: 48,
                          borderRadius: 6,
                          background: "#e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#64748b",
                        }}
                      >
                        SAREE
                      </div>
                    )}

                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                        <span
                          style={{
                            background: "#063b2a",
                            color: "#fff",
                            padding: "2px 7px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            fontFamily: "monospace",
                            letterSpacing: 0.5,
                          }}
                        >
                          PID #{prod.id}
                        </span>
                        {prod.colour && (
                          <span
                            style={{
                              background: "#e0f2fe",
                              color: "#0369a1",
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          >
                            Colour: {prod.colour}
                          </span>
                        )}
                      </div>
                      <strong style={{ fontSize: 13, color: "#1e293b", display: "block" }}>
                        {prod.name}
                      </strong>
                      <span style={{ fontSize: 11, color: "#64748b" }}>
                        ₹{prod.price.toLocaleString("en-IN")} • {prod.orderCount} orders
                      </span>
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: "bold", color: "#063b2a" }}>
                      {prod.unitsSold} sold
                    </div>
                    <div style={{ fontSize: 11, color: "#059669", fontWeight: 600 }}>
                      ₹{prod.revenue.toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* LEAST SELLING PRODUCTS */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 20,
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ background: "#f1f5f9", padding: 6, borderRadius: 8, color: "#64748b" }}>
                <TrendingDown size={18} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: "#063b2a" }}>Slow Moving / Least Selling</h3>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                  Items with 0 or low orders (consider promotions/discounts)
                </p>
              </div>
            </div>

            <Link
              to="/admin/coupons"
              style={{ fontSize: 12, color: "#063b2a", textDecoration: "none", fontWeight: 700 }}
            >
              Add Coupon →
            </Link>
          </div>

          {productPerformance.leastSelling.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              All products are performing actively.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {productPerformance.leastSelling.map((prod, idx) => (
                <div
                  key={prod.id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: "#fafafa",
                    borderRadius: 8,
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {prod.img ? (
                      <img
                        src={prod.img}
                        alt={prod.name}
                        style={{
                          width: 42,
                          height: 48,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #e2e8f0",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 42,
                          height: 48,
                          borderRadius: 6,
                          background: "#e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#64748b",
                        }}
                      >
                        SAREE
                      </div>
                    )}

                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                        <span
                          style={{
                            background: "#475569",
                            color: "#fff",
                            padding: "2px 7px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            fontFamily: "monospace",
                            letterSpacing: 0.5,
                          }}
                        >
                          PID #{prod.id}
                        </span>
                        {prod.colour && (
                          <span
                            style={{
                              background: "#f1f5f9",
                              color: "#334155",
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          >
                            Colour: {prod.colour}
                          </span>
                        )}
                      </div>
                      <strong style={{ fontSize: 13, color: "#1e293b", display: "block" }}>
                        {prod.name}
                      </strong>
                      <span style={{ fontSize: 11, color: "#64748b" }}>
                        ₹{prod.price.toLocaleString("en-IN")} • {prod.orderCount} orders
                      </span>
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 8px",
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        background: prod.unitsSold === 0 ? "#fee2e2" : "#fef3c7",
                        color: prod.unitsSold === 0 ? "#991b1b" : "#92400e",
                      }}
                    >
                      {prod.unitsSold} sold
                    </span>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                      {prod.unitsSold === 0 ? "Needs promotion" : `₹${prod.revenue.toLocaleString("en-IN")}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ==============================================================
          LATEST ORDERS LIST PREVIEW
      ============================================================== */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 17, color: "#063b2a" }}>Latest Orders</h3>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
              Most recent customer purchases in chronological order
            </p>
          </div>

          <button
            onClick={() => navigate("/admin/orders")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#063b2a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 14px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            <span>View All Orders in Order Manager</span>
            <ArrowRight size={14} />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
            <RefreshCw size={24} className="spin-icon" style={{ marginBottom: 10 }} />
            <p>Loading latest orders...</p>
          </div>
        ) : recentOrders.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
            No orders match the selected period.
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE VIEW */}
            <div className="dashboard-desktop-table" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th style={tableTh}>Order ID</th>
                    <th style={tableTh}>Customer</th>
                    <th style={tableTh}>Booking Date & Time</th>
                    <th style={tableTh}>Total Amount</th>
                    <th style={tableTh}>Payment Method</th>
                    <th style={tableTh}>Order Status</th>
                    <th style={tableTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order, idx) => (
                    <tr
                      key={order.id || idx}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: idx % 2 === 0 ? "#ffffff" : "#fbfcfd",
                      }}
                    >
                      <td style={tableTd}>
                        <strong style={{ color: "#063b2a", fontSize: 13 }}>
                          {getOrderCode(order)}
                        </strong>
                      </td>

                      <td style={tableTd}>
                        <div>
                          <strong>{order.full_name || "-"}</strong>
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>
                          {order.phone}
                          {order.city ? ` • ${order.city}` : ""}
                        </div>
                      </td>

                      <td style={tableTd}>
                        <span style={{ fontSize: 12, color: "#334155" }}>
                          {formatDateTimeDDMMYYYY(order.created_at)}
                        </span>
                      </td>

                      <td style={tableTd}>
                        <strong style={{ color: "#063b2a" }}>
                          ₹{parseFloat(order.total_amount || 0).toLocaleString("en-IN")}
                        </strong>
                      </td>

                      <td style={tableTd}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 8px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            background:
                              order.payment_method === "razorpay"
                                ? "#ecfdf5"
                                : order.payment_method === "cod"
                                ? "#e0f2fe"
                                : "#f3e8ff",
                            color:
                              order.payment_method === "razorpay"
                                ? "#047857"
                                : order.payment_method === "cod"
                                ? "#0369a1"
                                : "#6b21a8",
                          }}
                        >
                          {order.payment_method === "razorpay"
                            ? "⚡ Razorpay"
                            : order.payment_method === "cod"
                            ? "💵 COD"
                            : "📱 UPI"}
                        </span>
                      </td>

                      <td style={tableTd}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            borderRadius: 14,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "capitalize",
                            ...getOrderStatusStyle(order.order_status),
                          }}
                        >
                          ● {order.order_status || "Pending"}
                        </span>
                      </td>

                      <td style={tableTd}>
                        <button
                          onClick={() => navigate("/admin/orders")}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 5,
                            background: "#e8f0fe",
                            color: "#1a73e8",
                            border: "1px solid #c2dbfe",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILE ORDER CARDS (COMPACT VIEW) */}
            <div className="dashboard-mobile-cards">
              {recentOrders.map((order, idx) => (
                <div
                  key={order.id || idx}
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "12px 14px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <strong style={{ color: "#063b2a", fontSize: 13, fontFamily: "monospace" }}>
                      {getOrderCode(order)}
                    </strong>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: 12,
                        fontSize: 10,
                        fontWeight: 700,
                        ...getOrderStatusStyle(order.order_status),
                      }}
                    >
                      ● {order.order_status || "Pending"}
                    </span>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: "#1e293b" }}>{order.full_name || "-"}</span>
                    <span style={{ color: "#64748b" }}>{formatDateDDMMYYYY(order.created_at)}</span>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
                    <div>
                      <span style={{ fontSize: 11, color: "#64748b" }}>Amount: </span>
                      <strong style={{ color: "#063b2a", fontSize: 13 }}>
                        ₹{parseFloat(order.total_amount || 0).toLocaleString("en-IN")}
                      </strong>
                    </div>
                    <button
                      onClick={() => navigate("/admin/orders")}
                      style={{
                        padding: "5px 12px",
                        background: "#063b2a",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Manage →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* SPIN KEYFRAME & RESPONSIVE MOBILE STYLES */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 12px 10px !important;
          }
          .dashboard-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .dashboard-header h1 {
            font-size: 22px !important;
          }
          .dashboard-header-btn {
            width: 100% !important;
            justify-content: center !important;
          }
          .dashboard-filter-card {
            padding: 12px !important;
          }
          .dashboard-filter-presets {
            display: flex !important;
            overflow-x: auto !important;
            white-space: nowrap !important;
            padding-bottom: 6px !important;
            width: 100% !important;
            -webkit-overflow-scrolling: touch;
          }
          .dashboard-filter-presets button {
            flex-shrink: 0 !important;
            padding: 5px 12px !important;
            font-size: 11px !important;
          }
          .dashboard-filter-dates {
            width: 100% !important;
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
          }
          .dashboard-filter-dates input {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .dashboard-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }
          .dashboard-stat-card {
            padding: 12px 14px !important;
          }
          .dashboard-stat-value {
            font-size: 20px !important;
          }
          .dashboard-stat-sub {
            font-size: 11px !important;
          }
          .dashboard-analytics-row {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .dashboard-products-row {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .dashboard-desktop-table {
            display: none !important;
          }
          .dashboard-mobile-cards {
            display: flex !important;
            flex-direction: column !important;
            gap: 10px !important;
          }
          .dashboard-payment-grid {
            grid-template-columns: 1fr !important;
            gap: 6px !important;
          }
        }
        @media (min-width: 769px) {
          .dashboard-desktop-table {
            display: block !important;
          }
          .dashboard-mobile-cards {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

// ============================
// STYLES
// ============================
const statCardStyle = {
  background: "#fff",
  borderRadius: 12,
  padding: "18px 20px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const statCardLabel = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.5,
  color: "#64748b",
};

const tableTh = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 12,
  color: "#475569",
  fontWeight: 600,
};

const tableTd = {
  padding: "12px",
  verticalAlign: "middle",
};

export default AdminDashboard;